import { ClickAndCollectCards } from '@/components/order/shippingOptions/ClickAndCollect/ClickAndCollectCards';
import { SelectedClickAndCollectLocationCard } from '@/components/order/shippingOptions/ClickAndCollect/SelectedClickAndCollectLocationCard';
import ClickAndCollectionLocationQuery from '@/components/order/shippingOptions/ClickAndCollectionLocationQuery';

import useClickAndCollectLocationQuery from '@/hooks/order/useClickAndCollectLocationQuery';
import useClickAndCollectSelection from '@/hooks/order/useClickAndCollectSelection';
import { PATIENT_REFILL_PHARMACY_ROUTE, UMEDS_MARKETING } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { Alert, AlertTitle, Box, Button, IconButton, Link, Stack, toast, Typography } from '@montugroup/design-system';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Grid2, styled } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CheckoutOrderSummary from '@/components/order/order-summary/CheckoutOrderSummary';
import UmedsMarketingConsent from '@/components/order/order-summary/UmedsMarketingConsent';
import { CheckoutButton } from '@/components/order/shippingOptions/CheckoutButton';
import { ClickAndCollectAcknowledgePartialInventoryModal } from '@/components/order/shippingOptions/ClickAndCollect/ClickAndCollectAcknowledgePartialInventoryModal';
import ClickAndCollectMap from '@/components/order/shippingOptions/ClickAndCollect/ClickAndCollectMap';
import { RequestDifferentLocationModal } from '@/components/order/shippingOptions/ClickAndCollect/RequestDifferentLocationModal';
import { getPartialStockAlertMessage } from '@/components/order/shippingOptions/ShippingOptionCard/ShippingOptionAlertPartialStock';
import ValidIDCheckbox from '@/components/order/shippingOptions/ValidIDCheckbox';
import { UseOrDeleteExistingOrderModal } from '@/components/order/UseOrDeleteExistingOrderModal';
import getCheckoutData from '@/hooks/patient/refill/getCheckoutData';
import { managePersistedOrderDetails } from '@/hooks/patient/refill/persistedOrderDetails';
import useShopifyCheckout from '@/hooks/patient/refill/useShopifyCheckout';
import { formatPharmacyAddress } from '@/utils/address';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, translateMontuError, usePrevious } from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { useEmitUiEvent } from '@montugroup/data-collection';
import { useQueryClient } from '@tanstack/react-query';
import { isErrorResponse } from '@ts-rest/core';
import { ErrorBoundary } from 'react-error-boundary';

const getDistanceKm = (distanceFromCustomerInMeters?: number) =>
  distanceFromCustomerInMeters && distanceFromCustomerInMeters > 0
    ? Math.max(0.1, Number((distanceFromCustomerInMeters / 1000).toFixed(1)))
    : undefined;

const PageContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(5),
  alignSelf: 'stretch',
}));

const PageSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const logger = new Logger('ClickAndCollect');

const client = getTsApiV1Client();

export function ClickAndCollect() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const emitEvent = useEmitUiEvent();
  const { locationQuery, setLocationQuery } = useClickAndCollectLocationQuery();
  const clickAndCollectSelection = useClickAndCollectSelection({ addressOverride: locationQuery });
  const { isFetching } = clickAndCollectSelection;
  const clickAndCollectOptions = useMemo(() => {
    if (clickAndCollectSelection.isFetching) {
      return [];
    }

    return clickAndCollectSelection.isNegotiable ? clickAndCollectSelection.options : [clickAndCollectSelection.option];
  }, [clickAndCollectSelection]);
  const [isConfirmingSelection, setIsConfirmingSelection] = useState<boolean>(false);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<number | null>(null);
  const [confirmedPharmacyId, setConfirmedPharmacyId] = useState<number | null>(null);
  const [validIDCheckAccepted, setValidIdCheckAccepted] = useState(false);
  const [checkoutError, setCheckoutError] = useState<unknown>(null);
  const [isRequestDifferentLocationModalOpen, setIsRequestDifferentLocationModalOpen] = useState(false);
  const [hasRequestedDifferentLocation, setHasRequestedDifferentLocation] = useState(false);

  const persistedOrderDetails = managePersistedOrderDetails();
  const selectedData = persistedOrderDetails.getOrderDetails();
  const selectedProducts = selectedData?.selectedProducts;
  const discounts = selectedData?.discounts;
  const location = useLocation();
  const { orderId, orderCode } = location.state || {};

  const { placeOrderShopify, isSubmitting } = useShopifyCheckout({
    orderId: Number(orderId),
    orderCode: orderCode && String(orderCode),
    selectedProducts,
  });

  const transformedProductsForSummary =
    selectedProducts?.map((product) => ({
      id: product.id,
      data: {
        product_id: product.id,
        product_name: product.name,
        price: product.price,
        quantity: product.quantity,
        brand: product.brand,
        is_concession: product.is_concession,
      },
    })) ?? [];

  const checkoutData = getCheckoutData(transformedProductsForSummary, discounts ?? []);

  const clickAndCollectOptionsPharmacyIds = useMemo(() => {
    return clickAndCollectOptions.map((option) => option.pharmacy.id);
  }, [clickAndCollectOptions]);

  const confirmedClickAndCollectOption = useMemo(() => {
    return clickAndCollectOptions.find((option) => option.pharmacy.id === confirmedPharmacyId);
  }, [clickAndCollectOptions, confirmedPharmacyId]);

  const selectedClickAndCollectOption = useMemo(() => {
    return clickAndCollectOptions.find((option) => option.pharmacy.id === selectedPharmacyId);
  }, [clickAndCollectOptions, selectedPharmacyId]);

  const clickAndCollectLocations = useMemo(() => {
    return clickAndCollectOptions.map((option) => ({
      id: option.pharmacy.id,
      name: option.pharmacy.name,
      address: formatPharmacyAddress(option.pharmacyLocation.address),
      distanceKm: getDistanceKm(option.pharmacyLocation.distanceFromCustomerInMeters),
      selected: selectedPharmacyId === option.pharmacy.id,
      option,
      // TODO: Update this to just `hasInventory: option.hasInventory` after contract stops allowing `hasInventory: undefined`
      hasInventory: option.hasInventory !== false,
    }));
  }, [clickAndCollectOptions, selectedPharmacyId]);

  const handleConfirmingSelection = () => {
    if (selectedClickAndCollectOption?.hasInventory) {
      setConfirmedPharmacyId(selectedPharmacyId);
    } else {
      setIsConfirmingSelection(true);
    }
  };

  const handleCancelSelectionConfirmation = () => {
    setIsConfirmingSelection(false);
  };

  const handleConfirmSelection = () => {
    setIsConfirmingSelection(false);
    setConfirmedPharmacyId(selectedPharmacyId);
  };

  const handleSelectNewLocation = () => {
    if (isSubmitting) {
      return;
    }

    setConfirmedPharmacyId(null);
    setSelectedPharmacyId(null);
    setHasRequestedDifferentLocation(false);
  };

  const handleCheckout = async () => {
    try {
      if (!selectedPharmacyId || !confirmedClickAndCollectOption) {
        logger.error('Checkout blocked due to missing data');
        return;
      }

      await placeOrderShopify({
        deliveryOption: {
          ...confirmedClickAndCollectOption,
          displayName: DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].displayName,
          pharmacyId: confirmedClickAndCollectOption.pharmacy.id,
        },
      });
    } catch (error: unknown) {
      setCheckoutError(error);
      logger.error('Shipping options checkout failed', error);

      if (isErrorResponse(error)) {
        // NOTE: Refetching the patientRefill endpoint here is just incase the error was caused by an existing unpaid admin order which doesn't have a static error.code yet
        // TODO: Update this to only invalidate on specific error code once BE has implemented that
        queryClient.invalidateQueries({ queryKey: client.order.getPatientRefill.keys });

        if (isMontuError(error)) {
          return toast.error(
            translateMontuError({
              error: error.body.error,
              fallbackOverride: 'Failed to process checkout',
            }),
          );
        }
      }

      return toast.error('Failed to process checkout');
    }
  };

  // NOTE: Needed to use layout effect to select the shipping option before it's rendered unselected to prevent the UI flashing.
  // Locking to a non-negotiable option takes priority - it also auto confirms, since the customer isn't allowed to pick anything else
  useLayoutEffect(() => {
    if (clickAndCollectSelection.isFetching || selectedPharmacyId) {
      return;
    }

    if (!clickAndCollectSelection.isNegotiable) {
      const nonNegotiablePharmacyId = clickAndCollectSelection.option.pharmacy.id;
      setSelectedPharmacyId(nonNegotiablePharmacyId);
      setConfirmedPharmacyId(nonNegotiablePharmacyId);
      logger.info(`auto selecting and confirming non-negotiable pharmacy option: ${nonNegotiablePharmacyId}`, {
        clickAndCollectOptions,
      });
      return;
    }

    if (clickAndCollectOptionsPharmacyIds.length === 1) {
      setSelectedPharmacyId(clickAndCollectOptionsPharmacyIds[0]);
      logger.info(`auto selecting single available option: ${clickAndCollectOptionsPharmacyIds[0]}`, {
        clickAndCollectOptions,
      });
    }
  }, [clickAndCollectSelection, clickAndCollectOptions, clickAndCollectOptionsPharmacyIds, selectedPharmacyId]);

  // NOTE: The following logic (before the returns) is analytics related
  const previouslySelectedPharmacyId = usePrevious(selectedPharmacyId);

  useEffect(() => {
    if (selectedPharmacyId && selectedPharmacyId !== previouslySelectedPharmacyId) {
      emitEvent({
        component_name: 'ClickAndCollect',
        event_name: 'click_and_collect_select_option',
        id: 'click_and_collect_select_option',
        value: JSON.stringify({
          name: selectedClickAndCollectOption?.pharmacy.name,
          id: selectedClickAndCollectOption?.pharmacy.id,
          address: selectedClickAndCollectOption?.pharmacyLocation.address,
          distance: selectedClickAndCollectOption?.pharmacyLocation.distanceFromCustomerInMeters,
          // TODO: Update this to just `itemAvailability: selectedClickAndCollectOption?.hasInventory` after contract stops allowing `hasInventory: undefined`
          itemAvailability: getPartialStockAlertMessage(selectedClickAndCollectOption?.hasInventory !== false),
        }),
      });
    }
  }, [
    emitEvent,
    previouslySelectedPharmacyId,
    selectedClickAndCollectOption?.hasInventory,
    selectedClickAndCollectOption?.pharmacy.id,
    selectedClickAndCollectOption?.pharmacy.name,
    selectedClickAndCollectOption?.pharmacyLocation.address,
    selectedClickAndCollectOption?.pharmacyLocation.distanceFromCustomerInMeters,
    selectedPharmacyId,
  ]);

  const previousShownClickAndCollectOptionsPharmacyIds = usePrevious(clickAndCollectOptionsPharmacyIds);

  useEffect(() => {
    if (
      !isFetching &&
      clickAndCollectOptionsPharmacyIds.length &&
      new Set(clickAndCollectOptionsPharmacyIds).difference(new Set(previousShownClickAndCollectOptionsPharmacyIds))
        .size !== 0
    ) {
      const shownClickAndCollectOptions = clickAndCollectOptions.map((option, i) => {
        return {
          name: option.pharmacy.name,
          id: option.pharmacy.id,
          address: option.pharmacyLocation.address,
          distance: option.pharmacyLocation.distanceFromCustomerInMeters,
          // TODO: Update this to just `availability: option.hasInventory` after contract stops allowing `hasInventory: undefined`
          availability: option.hasInventory !== false ? 'all/1 day' : 'partial/3-7 days',
          hasInventory: Boolean(option.hasInventory),
          inventoryReservationGroupId: option.inventoryReservationId,
          i,
        };
      });

      shownClickAndCollectOptions.forEach((option) => {
        emitEvent({
          component_name: 'ClickAndCollect',
          event_name: 'click_and_collect_show_option',
          id: 'click_and_collect_show_option',
          value: JSON.stringify(option),
        });
      });
    }
  }, [
    clickAndCollectOptions,
    clickAndCollectOptionsPharmacyIds,
    emitEvent,
    isFetching,
    previousShownClickAndCollectOptionsPharmacyIds,
  ]);

  const previousLocationQuery = usePrevious(locationQuery);

  useEffect(() => {
    if (previousLocationQuery !== locationQuery) {
      // NOTE: This doesn't catch the first address used but that is the user profile address
      emitEvent({
        component_name: 'ClickAndCollect',
        event_name: 'click_and_collect_address_search',
        id: 'click_and_collect_address_search',
        value: JSON.stringify(locationQuery),
      });
    }
  }, [emitEvent, locationQuery, previousLocationQuery]);

  const displayedMapLocations = useMemo(
    () => (confirmedClickAndCollectOption ? [confirmedClickAndCollectOption] : clickAndCollectOptions),
    [clickAndCollectOptions, confirmedClickAndCollectOption],
  );

  return (
    <PageContent>
      <PageSection sx={{ alignItems: 'flex-start' }}>
        <IconButton
          size="small"
          sx={{
            padding: 0,
            '&.MuiButtonBase-root:focus': { outline: 'none' },
          }}
          onClick={() => navigate({ pathname: PATIENT_REFILL_PHARMACY_ROUTE })}
          disableRipple
        >
          <ChevronLeft /> &nbsp;
          <Typography variant="body1" fontWeight="500" sx={(theme) => ({ color: theme.palette.primary.main })}>
            Back to shipping options
          </Typography>
        </IconButton>
      </PageSection>
      <Grid2 container spacing={5} columns={{ xs: 1, md: 2 }}>
        <Grid2 size={{ xs: 1 }}>
          {!confirmedPharmacyId && !clickAndCollectSelection.isFetching && clickAndCollectSelection.isNegotiable && (
            <>
              <Typography variant="body1" fontWeight={600} marginBottom={3}>
                Select a location to collect
              </Typography>

              <Grid2 container spacing={4} alignItems="flex-start">
                <Grid2 size={{ xs: 12 }}>
                  <ClickAndCollectionLocationQuery
                    address={locationQuery}
                    onSelectedAddress={(address) => {
                      setSelectedPharmacyId(null);
                      setLocationQuery(address);
                    }}
                  />
                </Grid2>
              </Grid2>
            </>
          )}
          {/* TODO: This error boundary should be handled by the MapView component but first we need to move more of the map related logic into that component so everything can be caught */}
          <ErrorBoundary
            onError={(error) => logger.error('C&C map crashed and rendered error boundary', error)}
            fallbackRender={() => (
              <Stack justifyContent={'center'} alignItems={'center'} paddingTop={4}>
                <Alert severity={'warning'}>
                  <AlertTitle>Something went wrong displaying the map</AlertTitle>
                  <Typography>
                    However you can still search for locations and checkout normally, sorry for the inconvenience.
                  </Typography>
                </Alert>
              </Stack>
            )}
          >
            <Grid2 container>
              <Grid2 size={{ xs: 12 }}>
                <ClickAndCollectMap
                  locationQuery={locationQuery}
                  clickAndCollectOptions={displayedMapLocations}
                  selectedPharmacy={selectedClickAndCollectOption || null}
                  confirmedPharmacy={confirmedClickAndCollectOption || null}
                  handleMarkerClicked={isFetching ? () => {} : setSelectedPharmacyId}
                />
              </Grid2>
            </Grid2>
          </ErrorBoundary>
        </Grid2>

        <Grid2 size={{ xs: 1 }}>
          {confirmedClickAndCollectOption ? (
            <Stack spacing={3}>
              <Typography fontWeight={600}>Location selected</Typography>

              <SelectedClickAndCollectLocationCard
                pharmacyName={confirmedClickAndCollectOption.pharmacy.name}
                address={formatPharmacyAddress(confirmedClickAndCollectOption.pharmacyLocation.address)}
                deliveryRequirements={confirmedClickAndCollectOption.deliveryRequirements}
                // TODO: Update this to just `hasInventory={confirmedClickAndCollectOption.hasInventory}` after contract stops allowing `hasInventory: undefined`
                hasInventory={confirmedClickAndCollectOption.hasInventory !== false}
              />

              {!clickAndCollectSelection.isFetching &&
                !clickAndCollectSelection.isNegotiable &&
                !hasRequestedDifferentLocation && (
                  <Button
                    variant="text"
                    onClick={() => setIsRequestDifferentLocationModalOpen(true)}
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    sx={{ alignSelf: 'center' }}
                  >
                    Request a different location
                  </Button>
                )}

              {!clickAndCollectSelection.isFetching && clickAndCollectSelection.isNegotiable && (
                <Button disabled={isSubmitting} onClick={handleSelectNewLocation} variant="outlined" fullWidth>
                  Select a new location
                </Button>
              )}
            </Stack>
          ) : (
            <>
              <ClickAndCollectCards
                isPending={isFetching}
                hasLocations={clickAndCollectOptions.length > 0}
                cards={clickAndCollectLocations}
                onSelect={setSelectedPharmacyId}
              />
              <Stack gap={2}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!selectedPharmacyId}
                  onClick={handleConfirmingSelection}
                >
                  Confirm selection
                </Button>
                <Button variant="outlined" size="large" fullWidth onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Stack>
            </>
          )}
          {confirmedClickAndCollectOption && (
            <Stack marginTop={6}>
              <CheckoutOrderSummary
                checkoutData={checkoutData}
                isLoading={isFetching}
                selectedOption={confirmedClickAndCollectOption}
                slots={{
                  terms: (
                    <Stack gap={2}>
                      <ValidIDCheckbox
                        size="small"
                        value={validIDCheckAccepted}
                        onChange={setValidIdCheckAccepted}
                        deliveryText="collection"
                      />
                      <UmedsMarketingConsent size="small" />
                      <Typography variant={'caption'} marginLeft={0.5} marginTop={2}>
                        By proceeding, you agree with the{' '}
                        <Link href={UMEDS_MARKETING.TERMS_CONDITIONS} target="_blank" rel="noopener noreferrer">
                          uMeds Terms and Conditions
                        </Link>{' '}
                        and those of the pharmacy that will supply your medication.
                      </Typography>
                    </Stack>
                  ),
                }}
              />
              <CheckoutButton
                loading={isSubmitting}
                disabled={!validIDCheckAccepted}
                onClick={handleCheckout}
                error={checkoutError}
              />
            </Stack>
          )}
        </Grid2>
      </Grid2>
      <UseOrDeleteExistingOrderModal />
      <ClickAndCollectAcknowledgePartialInventoryModal
        open={isConfirmingSelection}
        onCancel={handleCancelSelectionConfirmation}
        onConfirm={handleConfirmSelection}
      />
      <RequestDifferentLocationModal
        open={isRequestDifferentLocationModalOpen}
        onClose={() => setIsRequestDifferentLocationModalOpen(false)}
        onSubmitted={() => setHasRequestedDifferentLocation(true)}
      />
    </PageContent>
  );
}

export default ClickAndCollect;
